/**
 * gRPC API 封装
 * 提供统一的接口来调用后端的 gRPC 服务
 */

// 基础的 fetch 请求封装
async function request(url, options = {}) {
    try {
        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            ...options,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}, statusText: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('API request failed:', error);
        throw error;
    }
}

// 游戏相关 API
export const gameApi = {
    /**
     * 获取游戏列表
     */
    async getGames(params = {}) {
        const {
            page = 1,
            pageSize = 10,
            search = '',
            categoryIds = [],
            tagIds = [],
            status = '1',
            sortBy = 'created_at',
            sortOrder = 'desc'
        } = params;

        const queryParams = new URLSearchParams({
            action: 'list',
            page: page.toString(),
            pageSize: pageSize.toString(),
            search,
            categoryIds: categoryIds.join(','),
            tagIds: tagIds.join(','),
            status,
            sortBy,
            sortOrder
        });

        return await request(`/api/grpc/game?${queryParams}`);
    },

    /**
     * 通过分类获取游戏列表
     */
    async getGamesByCategory(params = {}) {
        const {
            id = 0,
            slug,
            page = 1,
            pageSize = 10,
            language = 'LANG_EN_US'
        } = params;

        if (!slug) {
            throw new Error('slug parameter is required for getGamesByCategory');
        }

        const queryParams = new URLSearchParams({
            action: 'getGamesByCategory',
            id: id.toString(),
            slug,
            page: page.toString(),
            pageSize: pageSize.toString(),
            language
        });

        return await request(`/api/grpc/game?${queryParams}`);
    },

    /**
     * 获取单个游戏详情
     */
    async getGame(id) {
        if (!id) {
            throw new Error('id parameter is required for getGame');
        }

        const queryParams = new URLSearchParams({
            action: 'detail',
            id: id.toString()
        });

        return await request(`/api/grpc/game?${queryParams}`);
    },

    /**
     * 创建游戏
     */
    async createGame(gameData) {
        return await request('/api/grpc/game', {
            method: 'POST',
            body: JSON.stringify(gameData),
        });
    },

    /**
     * 搜索游戏
     */
    async searchGames(params = {}, abortController = null) {
        const {
            query = '',
            page = 1,
            page_size = 100,
            language = 'LANG_EN_US'
        } = params;

        if (!query.trim()) {
            return { data: { results: [], total: 0, page, page_size, query } };
        }

        const queryParams = new URLSearchParams({
            action: 'search',
            query: query.trim(),
            page: page.toString(),
            page_size: page_size.toString(),
            language
        });

        const options = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        };

        // 如果提供了AbortController，则添加signal
        if (abortController) {
            options.signal = abortController.signal;
        }

        return await request(`/api/grpc/game?${queryParams}`, options);
    },
};

// 分类相关 API
export const categoryApi = {
    /**
     * 获取分类列表
     */
    async getCategories(params = {}) {
        const {
            page = 1,
            pageSize = 10,
            search = '',
            parentId = 0,
            includeInactive = false
        } = params;

        const queryParams = new URLSearchParams({
            action: 'list',
            page: page.toString(),
            pageSize: pageSize.toString(),
            search,
            parentId: parentId.toString(),
            includeInactive: includeInactive.toString()
        });

        return await request(`/api/grpc/category?${queryParams}`);
    }
};

// 首页相关 API
export const homepageApi = {
    /**
     * 获取首页核心数据
     */
    async getHomePageCoreData(params = {}) {
        const {
            platform = 1,
            uid = 1234567890,
            language = 'LANG_EN_US'
        } = params;

        const queryParams = new URLSearchParams({
            action: 'getHomePageCoreData',
            platform: platform.toString(),
            uid: uid.toString(),
            language
        });

        return await request(`/api/grpc/homepage?${queryParams}`);
    }
};

// 编辑器相关 API
export const editorApi = {
    /**
     * 获取推荐游戏
     */
    async getFeaturedGames(params = {}) {
        const {
            page = 1,
            pageSize = 10,
            recommendationType = 'FEATURED_RECOMMENDATION_TYPE_TOP',
            language = 'LANG_EN_US'
        } = params;

        const queryParams = new URLSearchParams({
            action: 'getFeaturedGames',
            page: page.toString(),
            pageSize: pageSize.toString(),
            recommendationType,
            language
        });

        return await request(`/api/grpc/editor?${queryParams}`);
    }
};

// 批量分类游戏加载工具函数
export const categoryUtils = {
    /**
     * 批量加载多个分类的游戏数据
     */
    async loadCategoriesGames(categories, options = {}) {
        const {
            page = 1,
            pageSize = 30,
            language = 'LANG_EN_US'
        } = options;

        const promises = categories.map(async (category) => {
            try {
                const response = await gameApi.getGamesByCategory({
                    id: 0,
                    slug: category.slug,
                    page,
                    pageSize,
                    language
                });

                return {
                    ...category,
                    games: response.games || [],
                    total: response.total || 0,
                    success: true
                };
            } catch (error) {
                console.error(`Failed to load games for category ${category.name}:`, error);
                return {
                    ...category,
                    games: [],
                    total: 0,
                    success: false,
                    error: error.message
                };
            }
        });

        return await Promise.all(promises);
    }
};

// 默认导出所有 API
const grpcApi = {
    game: gameApi,
    category: categoryApi,
    homepage: homepageApi,
    editor: editorApi,
    utils: categoryUtils
};

export default grpcApi; 